/**
 * Attendance summary table component - Displays aggregated client attendance data
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useTranslation } from "@/hooks/useTranslation";
import { formatDuration } from "../utils";
import type { Dochadzka } from "../types";

interface ClientSummary {
  klientId: number;
  klientAlias: string;
  klientMeno: string | null;
  klientPriezvisko: string | null;
  sessionsCount: number;
  totalDuration: number;
  totalPrice: number;
  totalProfit?: number;
}

interface AttendanceSummaryTableProps {
  data: Dochadzka[];
  klienti?: {
    id: number;
    alias: string;
    meno: string | null;
    priezvisko: string | null;
  }[];
  isLoading: boolean;
}

export function AttendanceSummaryTable({
  data,
  klienti,
  isLoading,
}: AttendanceSummaryTableProps) {
  const { t } = useTranslation();
  const hasIncomeBreakdown = data.some(session => session.income !== undefined);

  // Calculate summary data grouped by client
  const clientSummaries: ClientSummary[] = data.reduce((acc, session) => {
    const existingClient = acc.find(
      client => client.klientId === session.klientId
    );

    // Handle potentially null duration and price values
    const sessionDuration = session.dlzkaTreningu ?? 0;
    const sessionPrice = session.income
      ? session.income.selectedGross
      : typeof session.cena === "string"
        ? parseFloat(session.cena) || 0
        : (session.cena ?? 0);
    const sessionProfit = session.income?.selectedProfit;

    if (existingClient) {
      existingClient.sessionsCount += 1;
      existingClient.totalDuration += sessionDuration;
      existingClient.totalPrice += sessionPrice;
      if (sessionProfit !== undefined) {
        existingClient.totalProfit =
          (existingClient.totalProfit ?? 0) + sessionProfit;
      }
    } else {
      acc.push({
        klientId: session.klientId,
        klientAlias:
          session.klientAlias ||
          klienti?.find(k => k.id === session.klientId)?.alias ||
          "",
        klientMeno:
          session.klientMeno ||
          klienti?.find(k => k.id === session.klientId)?.meno ||
          null,
        klientPriezvisko:
          session.klientPriezvisko ||
          klienti?.find(k => k.id === session.klientId)?.priezvisko ||
          null,
        sessionsCount: 1,
        totalDuration: sessionDuration,
        totalPrice: sessionPrice,
        totalProfit: sessionProfit,
      });
    }

    return acc;
  }, [] as ClientSummary[]);

  // Sort by total duration descending
  clientSummaries.sort((a, b) => b.totalDuration - a.totalDuration);

  const getClientDisplay = (client: ClientSummary) => {
    return (
      client.klientAlias ||
      `${client.klientMeno || ""} ${client.klientPriezvisko || ""}`.trim()
    );
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>{t("attendance:summary.clientSummary")}</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">
            {t("common:loading")}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("attendance:table.client")}</TableHead>
                  <TableHead className="text-right">
                    {t("attendance:summary.sessionsCount")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("attendance:summary.totalDuration")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t(
                      hasIncomeBreakdown
                        ? "attendance:summary.selectedIncome"
                        : "attendance:summary.totalRevenue"
                    )}
                  </TableHead>
                  {hasIncomeBreakdown && (
                    <TableHead className="text-right">
                      {t("attendance:summary.selectedProfit")}
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {clientSummaries.map(client => (
                  <TableRow key={client.klientId}>
                    <TableCell className="font-medium">
                      {getClientDisplay(client)}
                    </TableCell>
                    <TableCell className="text-right">
                      {client.sessionsCount}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatDuration(client.totalDuration.toString())}
                    </TableCell>
                    <TableCell className="text-right">
                      €{client.totalPrice.toFixed(2)}
                    </TableCell>
                    {hasIncomeBreakdown && (
                      <TableCell className="text-right">
                        €{(client.totalProfit ?? 0).toFixed(2)}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {clientSummaries.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={hasIncomeBreakdown ? 5 : 4}
                      className="text-center py-8 text-muted-foreground"
                    >
                      {t("attendance:table.noSessions")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
